import { px } from '@kerfjs/ui/css-values';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { SegmentedControl } from '@kerfjs/ui/segmented-control';
import { ToolbarControlGroup } from '@kerfjs/ui/toolbar-control-group';
import { ToolbarText } from '@kerfjs/ui/toolbar-text';
import type { WorkbenchMainToolbar } from '@kerfjs/ui/workbench';
import { FileText, ListChecks } from 'lucide';

import { ProfileGroup } from '../../../components/profile-group.js';
import { StatusChip } from '../../../components/status-chip.js';
import { primaryView } from '../../../components/ui-helpers.js';
import {
  libraryScope,
  libraryTab,
  orgSavings,
  savingsPeriod,
  searchExpanded,
  settingsPage,
  settingsTab,
  shellSearch,
  shellSearchTokens,
  titleEditing,
  vendorDetail,
  workspace,
} from '../../../data/state.js';
import { demandDetailsReadOnly } from '../../../lib/context.js';
import { savingsPeriodLabel } from '../../../lib/savings-period.js';
import { settingsLocation, settingsPageLabel } from '../../../lib/settings-nav.js';
import { DemandSegmentsControl } from '../../demand-editor/components/demand-segments-control.js';
import { SavingsActions } from '../../savings/savings-actions.js';
import { DemandTitleControl } from './demand-title-control.js';
import { PricingCheckGroup } from './pricing-check-group.js';
import { ProjectChatTitleControl } from './project-chat-title-control.js';
import { SearchField } from './search-field.js';
import { SelectionActionsGroup } from './selection-actions-group.js';

/**
 * The Workbench work-area toolbar (`mainToolbar`) with the view, demand, search,
 * selection, and account controls. The Workbench leads it with a collapsed
 * navigation rail's switcher and toggle, and trails it with a collapsed
 * assistant's toggle, so this owns no show/hide controls (HS2-AC9K4G).
 */
export function mainToolbarConfig({ view: v }: { view: string }): WorkbenchMainToolbar {
  const primary = primaryView(v);
  const activeVendor = v === 'vendors' ? vendorDetail.value : null;
  const search = <SearchField view={v} />;
  const searchExpandedNow =
    (primary === 'demands' ||
      primary === 'chat' ||
      primary === 'stats' ||
      v === 'library' ||
      v === 'vendors' ||
      (v === 'settings' && settingsPage.value === 'members')) &&
    (searchExpanded.value || shellSearch.value !== '' || shellSearchTokens.value.length > 0);
  // Search stays immediately before the profile control in both states
  // (HS2-HN1V71). The wrapping toolbar gives it another row when demand
  // actions are crowded. The search's grow basis preserves an editable width.
  // It stays `grow` while collapsed too: Kerf animates the group open from the
  // collapsed grow state's fixed basis, and the field follows it on every frame.
  // A `content` collapse has an `auto` basis, which cannot animate, so the group
  // would snap open (HS2-K1NYX5, KF-50K0JM).
  const searchGroup = (
    <ToolbarControlGroup
      label="Search"
      className="shell-search-group"
      content="search"
      focusRing="halo"
      expanded={searchExpandedNow}
      single={!searchExpandedNow}
      sizing="grow"
    >
      {search}
    </ToolbarControlGroup>
  );
  const location = settingsLocation(v, {
    area: settingsTab.value,
    page: settingsPage.value,
    libraryScope: libraryScope.value,
  });
  const title = location
    ? settingsPageLabel(location.area, location.page)
    : primary === 'stats'
      ? savingsPeriodLabel(savingsPeriod.value)
      : v === 'demands'
        ? 'Demands'
        : '';
  const onDemand = v === 'workspace' || v === 'intake';
  const demandStatus = (workspace.value?.demand as { status?: string } | undefined)?.status;
  const demandTitleText = (workspace.value?.demand as { title?: string } | undefined)?.title ?? 'Demand';
  const staticTitle = demandDetailsReadOnly(); // the title stays editable while decided (HS2-Y50H8E)
  const demandTitle = onDemand ? (
    <>
      {staticTitle ? <ToolbarText text={demandTitleText} /> : <DemandTitleControl />}
      {demandStatus !== undefined && !titleEditing.value ? (
        <ToolbarControlGroup label="Status" appearance="borderless">
          <StatusChip status={demandStatus} />
        </ToolbarControlGroup>
      ) : null}
    </>
  ) : null;
  const nameLead =
    activeVendor !== null ? (
      <ToolbarControlGroup label="Navigation" appearance="borderless" content="text">
        <button type="button" data-action="vendors-back">
          ← Vendors
        </button>
      </ToolbarControlGroup>
    ) : onDemand ? (
      demandTitle
    ) : v === 'conversations' ? (
      <ProjectChatTitleControl />
    ) : primary === 'stats' ? (
      // A "Savings" page title leads, then the site filter (HS2-HJ806N). It
      // yields to the filter and period title in a narrow toolbar.
      <>
        <ToolbarText text="Savings" id="savings-page-title" hideBelow={px(560)} />
        <SavingsActions savings={orgSavings.value} part="site" />
      </>
    ) : title !== '' ? (
      <ToolbarText text={title} />
    ) : null;
  return {
    label: 'Views',
    responsive: 'wrap',
    centerAlign: 'stretch',
    leading: nameLead ?? undefined,
    center:
      activeVendor !== null ? (
        <ToolbarText text={activeVendor.vendor.name} id="vendor-detail-title" headingLevel={1} />
      ) : primary === 'stats' ? (
        <ToolbarText text={title} id="savings-period-title" headingLevel={1} />
      ) : undefined,
    trailing: (
      <>
        {onDemand ? <DemandSegmentsControl /> : null}
        {v === 'library' ? (
          <ToolbarControlGroup label="Knowledge Base Sections" appearance="contained">
            <SegmentedControl
              id="lib-tabs"
              label="Knowledge Base sections"
              value={libraryTab.value}
              action="lib-tab"
              appearance="toolbar"
              shape="pill"
              size="small"
              choices={[
                {
                  value: 'facts',
                  label: 'Facts',
                  title: 'Facts',
                  content: <LucideIcon icon={ListChecks} name="Facts" />,
                },
                {
                  value: 'documents',
                  label: 'Documents',
                  title: 'Documents',
                  content: <LucideIcon icon={FileText} name="Documents" />,
                },
              ]}
            />
          </ToolbarControlGroup>
        ) : null}
        <PricingCheckGroup view={v} />
        <SelectionActionsGroup view={v} />
        {primary === 'stats' ? <SavingsActions savings={orgSavings.value} part="export" /> : null}
        {activeVendor === null ? searchGroup : null}
        <ProfileGroup />
      </>
    ),
  };
}
